import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCircle2 } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";

export default function NotificationsPage() {
  useSeo({ title: "Notifications | LUX", ...PRIVATE_SEO });
  const client = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["advertiser", "notifications"],
    queryFn: async () => (await api.get("/advertiser/notifications")).data,
  });

  useEffect(() => {
    api.post("/advertiser/notifications/read").then(() => {
      client.invalidateQueries({ queryKey: ["advertiser", "me"] });
    }).catch(() => {});
  }, [client]);

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <header>
        <p className="lux-label">Notifications</p>
        <h1 className="mt-2 font-serif text-4xl text-lux-text sm:text-5xl">What's new</h1>
      </header>
      {isLoading ? (
        <p className="text-sm text-lux-muted">Loading...</p>
      ) : !data?.notifications?.length ? (
        <section className="lux-card flex flex-col items-center px-6 py-16 text-center" data-testid="notifications-empty">
          <Bell className="h-8 w-8 text-lux-silver" aria-hidden="true" />
          <p className="mt-4 font-serif text-2xl text-lux-text">You're all caught up</p>
          <p className="mt-1 text-sm text-lux-muted">Important updates about your listing, verification and reports will appear here.</p>
        </section>
      ) : (
        <ul className="space-y-3">
          {data.notifications.map((n) => (
            <li key={n.id} className="lux-card flex items-start gap-3 p-5" data-testid="notifications-item">
              <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-lux-silver" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="flex items-center justify-between gap-3 font-serif text-lg text-lux-text">
                  <span>{n.title}</span>
                  <span className="text-xs text-lux-muted">{fmtDate(n.created_at, true)}</span>
                </p>
                <p className="mt-1 text-sm text-lux-text/85">{n.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
